import { useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { Loader2 } from 'lucide-react'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FullPageLoader } from '@/components/full-page-loader'
import { useAuth } from '@/hooks/use-auth'
import { signInWithEmail, signInWithGoogle } from '@/lib/firebase'

function explain(err: unknown): string {
  const code = (err as { code?: string })?.code ?? ''
  if (['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-email'].includes(code))
    return 'That email and password don’t match an account.'
  if (code === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes, then try again.'
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return ''
  if (code === 'auth/network-request-failed') return 'Can’t reach Firebase. Check your connection.'
  return 'Sign-in failed. Try again.'
}

export default function LoginPage() {
  const { user, loading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState<'email' | 'google' | null>(null)
  const [error, setError] = useState('')

  if (loading) return <FullPageLoader />
  if (user) return <Navigate to="/" replace />

  const run = async (kind: 'email' | 'google', action: () => Promise<unknown>) => {
    setBusy(kind)
    setError('')
    try {
      await action()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    run('email', () => signInWithEmail(email.trim(), password))
  }

  return (
    <div className="grid min-h-svh place-items-center bg-sidebar p-4">
      <div className="panel w-full max-w-sm p-7">
        <Logo className="h-6" />
        <h1 className="mt-6 text-xl font-semibold">Sign in to the admin panel</h1>
        <p className="mt-1 text-muted-foreground">Use your Metafor account.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && (
            <p className="text-destructive" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" size="lg" className="w-full" disabled={busy !== null}>
            {busy === 'email' && <Loader2 className="animate-spin" />}
            Sign in
          </Button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>

        <Button variant="outline" size="lg" className="w-full" disabled={busy !== null} onClick={() => run('google', signInWithGoogle)}>
          {busy === 'google' && <Loader2 className="animate-spin" />}
          Continue with Google
        </Button>
      </div>
    </div>
  )
}
